import { parseUnitToNum } from "../../utils/cssunitparser";

type SliderOptions = {
    containerSelector: string;
    itemsContainerSelector: string;
    itemSelector: string;

    indicatorSelector?: string;
    currentSlideIndex?: number;
    nextNavSelector?: string;
    prevNavSelector?: string;
    resizeListener?: boolean;
    sliderViewportSelector?: string;
};

/**
 * The viewport is an outer container, whose width depends on the section grid it populates.
 */

export class SliderHTMLElement extends HTMLElement {
    /**
     * True if autoplay is set.
     */
    protected autoplay: boolean;
    /**
     * Autoplay delay in milliseconds.
     */
    protected autoplayInterval = 5000;
    /**
     * True if autoplay should stop when hovering on the slider.
     */
    protected autoplayPauseOnHover = false;
    /**
    * Auxiliary function to be executed, if defined, within setActiveItem, to add some custom behavior
    */
    protected auxiliarySetActiveItemCallback: (index: number) => void;
    /**
     * True if the current slide should be positioned centered.
     * In that case, the transformX value is relative to the CSS variable --slide instead of being calculated via JS.
     */
    protected currentSlideCentered: boolean;
    /**
     * True if the slide takes the whole viewport width¹.
     * Also implies "resizeListener" to false behavior.
     */
    protected fullWSlide: boolean;
    /**
     * A function to be executed when clicking on an indicator instead of the default behavior (setting the active item).
     */
    protected indicatorCallback: (index: number) => void;
    /**
     * The CSS selector for the indicator.
     */
    protected indicatorSelector: string;
    /**
     * True if the slider should wrap like an infinite carousel.
     */
    protected infinite = false;
    /**
     * True if the infinite behavior is set up.
     */
    protected infiniteConfigured = false;
    /**
     * False if the infinite behavior shouldn't be set up until the active slide is changed by user input.
     */
    protected initInfiniteOnLoad = true;
    /**
     * Total slides number.
     */
    protected itemsTotal = 0;
    /**
     * Used for debug. Helpful when there are multiple sliders on the page.
     */
    protected sliderType: string;
    /**
     * The index from which it should start to count.
     * Usually 0 or 1.
     */
    protected startingSlideIndex = 0;

    /**
     * True if all slides fit in the viewport¹.
     * Doesn't apply if "fullWSlide" is true.
     */
    private allItemsDisplayed: boolean;
    /**
     * To store the interval to be cleared.
     */
    private autoplayHandler: ReturnType<typeof setInterval>;
    /**
     * Number of slide clones necessary for the infinite behavior.
     * If "currentSlideCentered" is true, there are this number of clones to the left, and this + 1 to the right.
     * Otherwise, the number of clones is applied to the right; at the left there's just one clone.
     */
    private clonesForInfinite = 0;
    /**
     * The CSS selector for the container of "itemsContainerSelector".
     */
    private readonly containerSelector: string;
    /**
     * Stores the index of the current active slide.
     */
    private currentSlideIndex = 0;
    /**
     * Stores the X coordinate of the starting point of the horizontal swipe action.
     */
    private dragStartX: number;
    /**
     * The CSS selector of the CSS variable storing the size of the gutter between slides.
     */
    private gutterSelector = "--gutter";
    /**
     * The CSS selector of the container of the slides.
     */
    private readonly itemsContainerSelector: string;
    /**
     * The CSS selector of a slide.
     */
    private readonly itemSelector: string;
    /**
     * The CSS selector of the next navigation button.
     */
    private readonly nextNavSelector: string;
    /**
     * The class that disables the CSS transition.
     * It's applied when we need to change the active slide without the user noticing the change, which is the trick behind the infinite behavior.
     */
    private noTransitionClass = "no-transition";
    /**
     * The CSS selector of the previous navigation button.
     */
    private readonly prevNavSelector: string;
    /**
     * Stores the previous width of this element.
     */
    private prevWidth: number = 0;
    /**
     * Defines if the resize listener should be enabled or not.
     */
    private readonly resizeListener = true;
    /**
     * Stores the resize observer.
     */
    private resizeObserver: ResizeObserver;
    /**
     * To store the resize timeout to be cleared.
     */
    private resizeTimeout: ReturnType<typeof setTimeout>;
    /**
     * The CSS selector of the viewport¹.
     * Used to check if "allItemsDisplayed" can be set to true.
     */
    private readonly sliderViewportSelector: string;

    private interactive: boolean;

    constructor(opts: SliderOptions) {
        super();

        this.containerSelector = opts.containerSelector;
        this.itemsContainerSelector = opts.itemsContainerSelector;
        this.itemSelector = opts.itemSelector;

        if (opts.currentSlideIndex) this.currentSlideIndex = opts.currentSlideIndex;
        if (opts.indicatorSelector) this.indicatorSelector = opts.indicatorSelector;
        if (opts.nextNavSelector) this.nextNavSelector = opts.nextNavSelector;
        if (opts.prevNavSelector) this.prevNavSelector = opts.prevNavSelector;
        if (opts.resizeListener) this.resizeListener = opts.resizeListener;
        if (opts.sliderViewportSelector) this.sliderViewportSelector = opts.sliderViewportSelector;

        this.resizeObserver = new ResizeObserver(this.resizeObserverCallback);
        this.interactive = true;
    }

    public connectedCallback(): void {
        this.enableInteractivity();
    }

    public disconnectedCallback(): void {
        this.disableInteractivity();
    }

    public attributeChangedCallback(name: string, old: string, v: string): void {
        if (name === 'data-edit-mode' && v !== old) {
            this.interactive = v !== 'true';
        }
    }

    protected reset = (activeItem?: number) => {
        document.fonts.ready.then(() => {
            if (this.itemsTotal === 0) {
                this.disableInteractivity();
                this.displayAllItemsIfPossible();
            } else {
                this.displayAllItemsIfPossible().then(() => {
                    if (!this.allItemsDisplayed) {
                        if (this.infinite && this.initInfiniteOnLoad) {
                            this.setUpInfinite(activeItem);
                        } else {
                            this.setActiveItem(activeItem ?? this.currentSlideIndex);
                        }
                    }
                });
    
                this.enableInteractivity();
            }
        })
    };

    private async displayAllItemsIfPossible(): Promise<void> {
        const element = <HTMLElement>this;
        return new Promise<void>((resolve: () => void) => {
            const container: HTMLElement = this.querySelector<HTMLElement>(this.containerSelector);
            const itemsContainer: HTMLElement = element.querySelector(this.itemsContainerSelector);
            itemsContainer.classList.add(this.noTransitionClass);

            // reset
            element.classList.remove("all-displayed");
            itemsContainer.classList.remove("all-displayed");
            this.dragHandler(true);
            this.allItemsDisplayed = false;

            if (!this.fullWSlide) {
                if (this.infiniteConfigured) this.cleanUpInfinite();

                let itemsContainerScrollWidth: number;
                let sliderViewportWidth: number;
                if (itemsContainer.childElementCount) {
                    const computedStyle = getComputedStyle(itemsContainer);
                    const padLeft = parseUnitToNum(computedStyle.paddingLeft) || 0;
                    itemsContainerScrollWidth =
                        itemsContainer.lastElementChild?.getBoundingClientRect().right -
                        itemsContainer.firstElementChild?.getBoundingClientRect().left +
                        padLeft;
                    sliderViewportWidth = this.sliderViewportSelector
                        ? element.querySelector(this.sliderViewportSelector).clientWidth
                        : element.clientWidth;
                }

                if (!itemsContainer.childElementCount || Math.round(itemsContainerScrollWidth) <= sliderViewportWidth) {
                    this.allItemsDisplayed = true;

                    element.classList.add("all-displayed");
                    itemsContainer.classList.add("all-displayed");

                    itemsContainer.style.transform = "";

                    container.onpointerdown = undefined;
                    container.onpointerup = undefined;
                }
            } else if (this.itemsTotal === 1) {
                this.allItemsDisplayed = true;

                element.classList.add("all-displayed");
                itemsContainer.classList.add("all-displayed");

                itemsContainer.style.transform = "";

                container.onpointerdown = undefined;
                container.onpointerup = undefined;
            }

            setTimeout(() => {
                itemsContainer.classList.remove(this.noTransitionClass);
                resolve();
            }, 100);
        });
    }

    private setActiveItem = (index: number) => {
        this.currentSlideIndex = index;
        this.style.setProperty("--slide", index.toString());

        const container: HTMLElement = this.querySelector(this.containerSelector);
        const itemsContainer: HTMLElement = this.querySelector(this.itemsContainerSelector);
        const itemList = Array.from(itemsContainer.children);

        let movement: number;

        // we are assuming that those with this.currentSlideCentered === true will also have this.infiniteConfigured === true, 
        // so movement calculation isn't necessary
        if (!(this.currentSlideCentered || this.fullWSlide)) {
            const computedStyle = getComputedStyle(itemsContainer);
            const computedMatrix = new DOMMatrix(computedStyle.transform);
            const prevTranslateX = computedMatrix.m41;
            const itemsContainerPadLeft = parseUnitToNum(computedStyle.paddingLeft) || 0;
            movement =
                container.getBoundingClientRect().x -
                itemList[index - this.startingSlideIndex].getBoundingClientRect().x + // TODO FIXME
                itemsContainerPadLeft;

            itemsContainer.style.transform = `translateX(${prevTranslateX + movement}px)`;
        }

        for (const el of itemList) {
            el.classList.remove("active", "out");
        }
        for (const el of itemList) {
            const i = itemList.indexOf(el);
            if (i < index) {
                el.classList.add("out");
            }
        }
        itemList[index - this.startingSlideIndex]?.classList.add("active");

        if (this.indicatorSelector) {
            const indicators = Array.from(this.querySelectorAll(this.indicatorSelector));
            for (const el of indicators) {
                el.classList.remove("active");
            }
            const indicatorIndex = !this.infinite
                ? index - this.startingSlideIndex
                : (index - this.startingSlideIndex - itemList.findIndex(el => !el.classList.contains("cloned"))) %
                  this.itemsTotal;
            indicators[indicatorIndex]?.classList.add("active");
        }

        if (!this.infiniteConfigured) {
            const isStart = index === this.startingSlideIndex;
            const isEnd = this.fullWSlide
                ? index === this.itemsTotal
                : itemList[itemList.length - 1].getBoundingClientRect().right + movement <= container.getBoundingClientRect().right; // TODO fixme
            const prev = this.querySelector(this.prevNavSelector);
            const next = this.querySelector(this.nextNavSelector);
            if (isStart) {
                prev?.setAttribute("disabled", "disabled");
                next?.removeAttribute("disabled");
            } else if (isEnd) {
                prev?.removeAttribute("disabled");
                next?.setAttribute("disabled", "disabled");
                this.disableAutoplay();
                this.disablePauseOnHover();
            } else {
                prev?.removeAttribute("disabled");
                next?.removeAttribute("disabled");
            }
        }

        if (this.auxiliarySetActiveItemCallback) {
            this.auxiliarySetActiveItemCallback(index);
        }
    };

    /**
     * R E S I Z E
     */
    private resizeObserverCallback = (entries: ResizeObserverEntry[]) => {
        for (const entry of entries) { // should be just one entry
            const size = entry.contentRect?.width || entry.contentBoxSize[0].inlineSize
            if (size === 0 || this.prevWidth === size) {
                return;
            }
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = setTimeout(this.reset, 200);
            this.prevWidth = size;
        }
    }

    /**
     * N A V I G A T I O N
     */
    private clickHandler = (e: PointerEvent) => {
        const clickElement = e.target as HTMLElement;
        if (this.prevNavSelector && clickElement.closest(this.prevNavSelector)) {
            this.prev();
        } else if (this.nextNavSelector && clickElement.closest(this.nextNavSelector)) {
            this.next();
        } else if (this.indicatorSelector && clickElement.closest(this.indicatorSelector)) {
            const index = Array.from(this.querySelectorAll(this.indicatorSelector)).findIndex(
                el => el === clickElement.closest(this.indicatorSelector)
            );
            if (this.indicatorCallback) {
                this.indicatorCallback(index + this.startingSlideIndex + this.clonesForInfinite);
            } else {
                this.setActiveItem(index + this.startingSlideIndex + this.clonesForInfinite);
            }
        }
    };

    private dragHandler = (enable?: boolean) => {
        const container: HTMLElement = this.querySelector<HTMLElement>(this.containerSelector);
        if (!enable) {
            container.onpointerdown = undefined;
            container.onpointerup = undefined;
        } else if (!this.allItemsDisplayed) {
            container.onpointerdown = this.handlePointerDown;
            container.onpointerup = this.handlePointerUp;
        }
    }

    private enableInteractivity(): void {
        this.onclick = this.clickHandler;

        if (!this.fullWSlide || !this.resizeListener) {
            this.resizeObserver.observe(this);
        }

        this.dragHandler(true);

        this.enableAutoplay();
        this.enablePauseOnHover();
    }

    private disableInteractivity(): void {
        this.onclick = undefined;
        
        this.resizeObserver.disconnect();
        
        this.dragHandler(false);

        this.disableAutoplay();
        this.disablePauseOnHover();
    }

    private handlePointerDown = (e: PointerEvent) => { this.dragStartX = e.clientX };
    private handlePointerUp = (e: PointerEvent) => {
        if (this.interactive) {
            const prevCondition = this.prevNavSelector
                ? !this.querySelector(this.prevNavSelector).hasAttribute("disabled")
                : !this.infiniteConfigured &&
                !this.querySelector(this.indicatorSelector).parentElement.firstElementChild.classList.contains("active");

            const nextCondition = this.nextNavSelector
                ? !this.querySelector(this.nextNavSelector).hasAttribute("disabled")
                : !this.infiniteConfigured &&
                !this.querySelector(this.indicatorSelector).parentElement.lastElementChild.classList.contains("active");

            if (this.dragStartX - e.clientX < -50 && prevCondition) {
                this.prev();
            } else if (this.dragStartX - e.clientX > 50 && nextCondition) {
                this.next();
            }
        }
    };

    private next = async (): Promise<void> => {
        if (this.infinite) {
            await this.checkInfinite("right");
        }
        this.setActiveItem(this.currentSlideIndex + 1);
    };

    private prev = async (): Promise<void> => {
        if (this.infinite) {
            await this.checkInfinite("left");
        }
        this.setActiveItem(this.currentSlideIndex - 1);
    };

    /**
     * A U T O P L A Y
     */
    protected enableAutoplay = (): void => {
        if (this.autoplay) {
            this.disableAutoplay();
            this.autoplayHandler = setInterval(() => {
                if (this.interactive) {
                    this.next()
                }
            }, this.autoplayInterval);
        }
    };
    protected disableAutoplay = (): void => {
        clearInterval(this.autoplayHandler);
    };

    protected enablePauseOnHover = (): void => {
        if (this.autoplayPauseOnHover && this.autoplay) {
            this.onmouseover = this.disableAutoplay;
            this.onmouseout = this.enableAutoplay;
        }
    };
    protected disablePauseOnHover = (): void => {
        this.onmouseover = undefined;
        this.onmouseout = undefined;
    };

    /**
     * I N F I N I T E
     */
    protected async setUpInfinite(index?: number): Promise<void> {
        const element = <HTMLElement>this;
        return new Promise<void>((resolve: () => void) => {
            if (this.infiniteConfigured) this.cleanUpInfinite();

            if (this.allItemsDisplayed === false) {
                const itemsContainer: HTMLElement = element.querySelector(this.itemsContainerSelector);
                itemsContainer.classList.add(this.noTransitionClass);
                const items = Array.from(itemsContainer.children);
                const itemWidth = itemsContainer.querySelector(`${this.itemSelector}:not(.active)`)?.clientWidth;
                const gutter =
                    parseUnitToNum(getComputedStyle(itemsContainer)?.getPropertyValue(this.gutterSelector)) || 0;
                itemsContainer.style.transform = "";
                const sliderViewportWidth = this.sliderViewportSelector
                    ? element.querySelector(this.sliderViewportSelector).clientWidth
                    : element.clientWidth;

                let activeItem: number;

                if (this.currentSlideCentered) {
                    this.clonesForInfinite = Math.ceil(
                        ((sliderViewportWidth - itemWidth) / 2 - gutter) / (itemWidth + gutter)
                    );
                    let i = 0;
                    while (i < this.clonesForInfinite) {
                        const cloneBefore: Node = items[items.length - (i + 1)].cloneNode(true);
                        (<HTMLElement>cloneBefore).classList.add("cloned");
                        (<HTMLElement>cloneBefore).setAttribute("aria-hidden", "true");
                        itemsContainer.prepend(cloneBefore);
                        const cloneAfter: Node = items[i].cloneNode(true);
                        (<HTMLElement>cloneAfter).classList.add("cloned");
                        (<HTMLElement>cloneAfter).setAttribute("aria-hidden", "true");
                        itemsContainer.append(cloneAfter);
                        i++;
                    }
                    const cloneAfter: Node = items[i].cloneNode(true);
                    (<HTMLElement>cloneAfter).classList.add("cloned");
                    (<HTMLElement>cloneAfter).setAttribute("aria-hidden", "true");
                    itemsContainer.append(cloneAfter);
                    activeItem = this.clonesForInfinite + this.currentSlideIndex;
                } else {
                    this.clonesForInfinite = Math.ceil(sliderViewportWidth / (itemWidth + gutter));
                    const cloneBefore: Node = items[items.length - 1].cloneNode(true);
                    (<HTMLElement>cloneBefore).classList.add("cloned");
                    (<HTMLElement>cloneBefore).setAttribute("aria-hidden", "true");
                    itemsContainer.prepend(cloneBefore);
                    const cloneAfter: Node = items[0].cloneNode(true);
                    (<HTMLElement>cloneAfter).classList.add("cloned");
                    (<HTMLElement>cloneAfter).setAttribute("aria-hidden", "true");
                    itemsContainer.append(cloneAfter);
                    let i = 1;
                    while (i < this.clonesForInfinite) {
                        const cloneAfter: Node = items[i].cloneNode(true);
                        (<HTMLElement>cloneAfter).classList.add("cloned");
                        (<HTMLElement>cloneAfter).setAttribute("aria-hidden", "true");
                        itemsContainer.append(cloneAfter);
                        i++;
                    }
                    activeItem = 1 + (index ?? this.currentSlideIndex);
                }

                this.querySelector(this.prevNavSelector)?.removeAttribute("disabled");
                this.querySelector(this.nextNavSelector)?.removeAttribute("disabled");

                this.infiniteConfigured = true;
                this.setActiveItem(activeItem);

                setTimeout(() => {
                    itemsContainer.classList.remove(this.noTransitionClass);
                    resolve();
                }, 100);
            }
        });
    }

    protected cleanUpInfinite = () => {
        this.clonesForInfinite = 0;
        this.infiniteConfigured = false;

        const items = Array.from(this.querySelectorAll(this.itemSelector));
        if (items.length) {
            const firstNoClone = items.findIndex(i => !i.classList.contains("cloned"));

            for (const i of items) {
                i.classList.contains("cloned") && i.parentElement.removeChild(i);
            }

            const index =
                this.currentSlideIndex > this.startingSlideIndex
                    ? this.currentSlideIndex - firstNoClone
                    : this.startingSlideIndex;
            this.setActiveItem(index);
        }
    };

    private checkInfinite = async (direction: "right" | "left"): Promise<void> => {
        if (!this.infiniteConfigured) {
            await this.setUpInfinite();
        }
        return new Promise<void>(resolve => {
            const itemsContainer: Element = this.querySelector(this.itemsContainerSelector);
            const startingOffset = (this.currentSlideCentered ? this.clonesForInfinite : 1) + this.startingSlideIndex;

            if (this.currentSlideIndex - startingOffset <= 0 && direction === "left") {
                itemsContainer.classList.add(this.noTransitionClass);
                this.setActiveItem(this.itemsTotal + startingOffset);
            } else if (this.currentSlideIndex - startingOffset >= this.itemsTotal && direction === "right") {
                itemsContainer.classList.add(this.noTransitionClass);
                this.setActiveItem(startingOffset);
            }
            setTimeout(() => {
                itemsContainer.classList.remove(this.noTransitionClass);
                resolve();
            }, 100);
        });
    };

    /**
     * R A N D O M   S T A T I C   S L I D E
     */
    protected setRandomSlide(): void {
        this.disableInteractivity();

        // Set a random slide
        const randomIndex = Math.floor(Math.random() * this.itemsTotal) + this.startingSlideIndex;
        this.setActiveItem(randomIndex);
    }
    protected restoreSliderBehavior(): void {
        this.enableInteractivity();
        this.reset(this.startingSlideIndex);
    }
}
